import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Check, X, Clock, AlertTriangle, User, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkflowPanel = ({ actionFormationId, dossierFinanceurId, typeWorkflow, formateurs, currentUser }) => {
  const [tasks, setTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newTask, setNewTask] = useState({ tache: '', date_objectif: '', assigne_a_id: '', assigne_a_nom: '' });

  const fetchTasks = useCallback(async () => {
    try {
      let url = `${API_URL}/workflows?action_formation_id=${actionFormationId}&type_workflow=${typeWorkflow}`;
      if (dossierFinanceurId) url += `&dossier_financeur_id=${dossierFinanceurId}`;
      const resp = await axios.get(url);
      setTasks(resp.data);
    } catch {}
  }, [actionFormationId, dossierFinanceurId, typeWorkflow]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleAdd = async () => {
    if (!newTask.tache.trim()) { toast.error('Saisissez une tâche'); return; }
    try {
      await axios.post(`${API_URL}/workflows`, {
        action_formation_id: actionFormationId,
        dossier_financeur_id: dossierFinanceurId || null,
        type_workflow: typeWorkflow,
        tache: newTask.tache.trim(),
        date_objectif: newTask.date_objectif,
        assigne_a_id: newTask.assigne_a_id || '',
        assigne_a_nom: newTask.assigne_a_nom || '',
      });
      setNewTask({ tache: '', date_objectif: '', assigne_a_id: '', assigne_a_nom: '' });
      setShowForm(false);
      fetchTasks();
      toast.success('Tâche ajoutée');
    } catch { toast.error('Erreur'); }
  };

  const toggleDone = async (task) => {
    try {
      await axios.patch(`${API_URL}/workflows/${task.id}`, { fait: !task.fait });
      fetchTasks();
    } catch {}
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_URL}/workflows/${taskId}`);
      fetchTasks();
    } catch {}
  };

  const getRetardLabel = (task) => {
    if (task.fait || !task.date_objectif) return null;
    const today = new Date();
    today.setHours(0,0,0,0);
    const obj = parseISO(task.date_objectif);
    const diff = differenceInDays(obj, today);
    if (diff < 0) return { label: `${Math.abs(diff)}j de retard`, color: 'text-red-600 bg-red-50' };
    if (diff === 0) return { label: "Aujourd'hui", color: 'text-amber-600 bg-amber-50' };
    if (diff <= 3) return { label: `J-${diff}`, color: 'text-amber-500 bg-amber-50' };
    return null;
  };

  const doneCount = tasks.filter(t => t.fait).length;

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-500" />
            {typeWorkflow === 'pedagogique' ? 'Workflow Pédagogique' : 'Workflow Administratif'}
            <span className="text-xs font-normal text-slate-400 ml-1">({doneCount}/{tasks.length} réalisée{doneCount > 1 ? 's' : ''})</span>
          </h2>
          <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => setShowForm(!showForm)} data-testid={`add-wf-${typeWorkflow}`}>
            <Plus className="w-3 h-3 mr-1" /> Nouvelle tâche
          </Button>
        </div>

        {/* Add form */}
        {showForm && (
          <div className="border border-indigo-200 rounded-lg p-3 mb-4 bg-indigo-50/30 space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Tâche à réaliser</Label>
              <Input
                value={newTask.tache}
                onChange={(e) => setNewTask({ ...newTask, tache: e.target.value })}
                placeholder="Description de la tâche..."
                className="h-8 text-sm"
                data-testid={`wf-task-input-${typeWorkflow}`}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Date d'objectif</Label>
                <Input
                  type="date"
                  value={newTask.date_objectif}
                  onChange={(e) => setNewTask({ ...newTask, date_objectif: e.target.value })}
                  className="h-8 text-xs"
                  data-testid={`wf-date-input-${typeWorkflow}`}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Attribuer à</Label>
                <Select
                  value={newTask.assigne_a_id || 'self'}
                  onValueChange={(v) => {
                    if (v === 'self') {
                      setNewTask({ ...newTask, assigne_a_id: '', assigne_a_nom: '' });
                    } else {
                      const f = formateurs?.find(f => f.id === v);
                      setNewTask({ ...newTask, assigne_a_id: v, assigne_a_nom: f ? `${f.nom} ${f.prenom}` : '' });
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Moi-même" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Moi-même (par défaut)</SelectItem>
                    {(formateurs || []).map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.nom} {f.prenom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button type="button" size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700" onClick={handleAdd} data-testid={`wf-submit-${typeWorkflow}`}>Ajouter</Button>
            </div>
          </div>
        )}

        {/* Task list */}
        {tasks.length === 0 && !showForm && (
          <p className="text-xs text-slate-400 italic">Aucune tâche. Cliquez sur "Nouvelle tâche" pour commencer.</p>
        )}

        <div className="space-y-1">
          {tasks.map(task => {
            const retard = getRetardLabel(task);
            return (
              <div
                key={task.id}
                className={`flex items-start gap-3 px-3 py-2.5 rounded-md border transition-colors group ${task.fait ? 'bg-emerald-50/50 border-emerald-200' : retard?.color.includes('red') ? 'bg-red-50/30 border-red-200' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <button
                  type="button"
                  className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${task.fait ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 hover:border-indigo-400'}`}
                  onClick={() => toggleDone(task)}
                  data-testid={`wf-toggle-${task.id}`}
                >
                  {task.fait && <Check className="w-3 h-3 text-white" />}
                </button>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${task.fait ? 'line-through text-slate-400' : 'text-slate-700 font-medium'}`}>{task.tache}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
                    <span>Par {task.redacteur_nom} le {task.date_redaction}</span>
                    {task.assigne_a_nom && task.assigne_a_id !== task.redacteur_id && (
                      <span className="flex items-center gap-0.5"><User className="w-2.5 h-2.5" /> {task.assigne_a_nom}</span>
                    )}
                    {task.date_objectif && (
                      <span>Objectif : {format(parseISO(task.date_objectif), 'd MMM yyyy', { locale: fr })}</span>
                    )}
                    {task.fait && task.date_realisation && (
                      <span className="text-emerald-600">Réalisé le {task.date_realisation}</span>
                    )}
                  </div>
                </div>

                {retard && (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${retard.color}`}>
                    <AlertTriangle className="w-3 h-3 inline mr-0.5" />{retard.label}
                  </span>
                )}

                <button
                  type="button"
                  className="text-red-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                  onClick={() => deleteTask(task.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default WorkflowPanel;
